import { z } from 'zod';

import { labelsSchema, slugSchema, uuidSchema } from '../common/primitives';
import {
  type NodeAllocation,
  type NodeCapacity,
  calculateFreeCapacity,
  fitsIntoCapacity,
  resourceRequestSchema,
} from '../fleet/capacity';
import { type NodeMetrics, diskUsageRatio, memoryUsageRatio } from '../fleet/metrics';
import { type NodeStatus, isSchedulableStatus } from '../fleet/node-status';

/**
 * Platzierungslogik: Auf welchem Node landet eine neue Site?
 *
 * Zweistufig, wie es Kubernetes und Nomad machen:
 *
 *   1. FILTER  — harte Ausschlusskriterien. Ein Node fällt raus oder bleibt drin.
 *   2. SCORE   — gewichtete Bewertung der verbliebenen Kandidaten, 0 bis 1.
 *
 * Diese Datei enthält ausschließlich reine Funktionen. Das ist Absicht: Die
 * Platzierung ist die geschäftskritischste Entscheidung des Masters und muss
 * ohne Datenbank, ohne Netzwerk und ohne Nest-Kontext gegen synthetische
 * Flotten testbar sein.
 */

/** Eingabedaten eines Kandidaten. Nur das, was für die Entscheidung zählt. */
export interface PlacementCandidate {
  readonly nodeId: string;
  readonly hostname: string;
  readonly region: string;
  readonly labels: Readonly<Record<string, string>>;
  readonly status: NodeStatus;
  /** Administratives Flag; überstimmt einen ansonsten platzierbaren Status. */
  readonly schedulable: boolean;
  readonly capacity: NodeCapacity;
  readonly allocated: NodeAllocation;
  /** Messwerte des letzten Heartbeats. `null`, wenn noch keiner einging. */
  readonly metrics: NodeMetrics | null;
  /** Alter des letzten Heartbeats in Millisekunden. `null` = nie empfangen. */
  readonly heartbeatAgeMs: number | null;
  /**
   * Meldet die Systemprüfung des Nodes eine harte Verletzung?
   *
   * Bis M4a wurde der Preflight-Bericht gespeichert und angezeigt, aber nirgends
   * ausgewertet — ein Node mit fehlendem Ingress-Netz oder totem Datenbankserver
   * bekam weiter Bestellungen und ließ sie mitten im Provisioning scheitern. Der
   * Bericht sagt genau das, was die Platzierung wissen will: Kann dieser Server
   * überhaupt eine Site aufnehmen?
   */
  readonly preflightFailed: boolean;
  /**
   * Liegt die Agent-Version unter dem, was der Master voraussetzt?
   *
   * Seit M4b holen sich Worker ihre Software selbst vom Master ab. Zwischen dem
   * Bereitstellen eines neuen Artefakts und dem Zeitpunkt, an dem der letzte
   * Worker nachgezogen hat, liegen bis zu fünf Minuten — und wenn in dieser
   * Spanne ein Master mit neuerem Spezifikationsformat startet, lehnt ein
   * zurückgebliebener Agent jede Zuweisung **permanent** ab
   * (`SITE_SPEC_REJECTED`). Eine Site auf so einem Node zu platzieren heißt,
   * sie sofort als fehlgeschlagen zu markieren.
   *
   * Deshalb wird derselbe Vergleich, den die Registrierung schon anstellt, hier
   * ein zweites Mal angewandt: Ein Node mit zu altem Agent wartet, statt zu
   * scheitern.
   */
  readonly agentVersionOutdated: boolean;
  /** Images, die auf diesem Node bereits lokal vorliegen. */
  readonly cachedImages: readonly string[];
}

/** Anforderungen der zu platzierenden Site. */
export const placementRequestSchema = z.object({
  siteId: uuidSchema,
  region: slugSchema,
  resources: resourceRequestSchema,
  /** Labels, die ein Node zwingend tragen muss (exakte Übereinstimmung). */
  requiredLabels: labelsSchema.default({}),
  /** Images des Blueprints — für die Bewertung der Image-Affinität. */
  requiredImages: z.array(z.string()).default([]),
  /** Node, der explizit ausgeschlossen wird (z. B. bei einer Migration weg von ihm). */
  excludeNodeIds: z.array(uuidSchema).default([]),
});

export type PlacementRequest = z.infer<typeof placementRequestSchema>;

/** Gründe, aus denen ein Kandidat ausgeschlossen wurde. Für Diagnose und Fehlermeldung. */
export const REJECTION_REASONS = {
  statusNotSchedulable: 'status-not-schedulable',
  administrativelyDisabled: 'administratively-disabled',
  agentVersionOutdated: 'agent-version-outdated',
  preflightFailed: 'preflight-failed',
  regionMismatch: 'region-mismatch',
  labelMismatch: 'label-mismatch',
  insufficientCapacity: 'insufficient-capacity',
  heartbeatStale: 'heartbeat-stale',
  explicitlyExcluded: 'explicitly-excluded',
  overloaded: 'overloaded',
} as const;

export type RejectionReason = (typeof REJECTION_REASONS)[keyof typeof REJECTION_REASONS];

/** Menschenlesbare Begründungen — werden 1:1 in Logs und API-Fehler übernommen. */
export const REJECTION_REASON_MESSAGES: Record<RejectionReason, string> = {
  [REJECTION_REASONS.statusNotSchedulable]: 'Node-Status erlaubt keine Platzierung.',
  [REJECTION_REASONS.administrativelyDisabled]:
    'Node ist administrativ aus der Platzierung genommen.',
  [REJECTION_REASONS.agentVersionOutdated]:
    'Agent-Version des Nodes liegt unter der Mindestanforderung. Der Node zieht binnen weniger Minuten selbst nach; ist das Artefakt auf dem Master hinterlegt?',
  [REJECTION_REASONS.preflightFailed]:
    'Systemprüfung des Nodes meldet eine harte Verletzung. Der Bericht steht unter GET /fleet/nodes/<id>.',
  [REJECTION_REASONS.regionMismatch]: 'Node liegt in einer anderen Region.',
  [REJECTION_REASONS.labelMismatch]: 'Node erfüllt die geforderten Labels nicht.',
  [REJECTION_REASONS.insufficientCapacity]: 'Freie Kapazität des Nodes reicht nicht aus.',
  [REJECTION_REASONS.heartbeatStale]: 'Letzter Heartbeat ist zu alt.',
  [REJECTION_REASONS.explicitlyExcluded]: 'Node wurde für diese Anfrage ausgeschlossen.',
  [REJECTION_REASONS.overloaded]: 'Node ist laut Messwerten bereits überlastet.',
};

export interface RejectedCandidate {
  readonly nodeId: string;
  readonly hostname: string;
  readonly reason: RejectionReason;
  readonly message: string;
}

export interface ScoredCandidate {
  readonly nodeId: string;
  readonly hostname: string;
  /** Gesamtbewertung von 0 bis 1; höher ist besser. */
  readonly score: number;
  /** Einzelbeiträge, damit eine Entscheidung im Nachhinein erklärbar bleibt. */
  readonly breakdown: Readonly<Record<string, number>>;
}

export interface PlacementResult {
  readonly selected: ScoredCandidate | null;
  readonly ranked: readonly ScoredCandidate[];
  readonly rejected: readonly RejectedCandidate[];
}

/**
 * Gewichte der Bewertungsfaktoren. Summe muss 1 ergeben.
 *
 * Die Verteilung folgt der Betriebserfahrung klassischer Hoster: Arbeitsspeicher
 * ist der Engpass, der zuerst weh tut (PHP-FPM-Pools, MariaDB-Buffer), Platte
 * danach, CPU erst deutlich später. Die Image-Affinität ist gering gewichtet,
 * verkürzt aber die Provisionierungszeit spürbar, wenn sie den Ausschlag gibt.
 */
export const SCORING_WEIGHTS = {
  freeMemory: 0.35,
  freeDisk: 0.25,
  cpuHeadroom: 0.2,
  siteSlots: 0.1,
  imageAffinity: 0.1,
} as const;

/** Ab dieser gemessenen Auslastung gilt ein Node trotz freier Reservierung als überlastet. */
export const OVERLOAD_THRESHOLDS = {
  cpuPercent: 90,
  memoryRatio: 0.92,
  diskRatio: 0.9,
} as const;

/**
 * Schwellen, ab denen ein Node als überlastet gilt.
 *
 * Als Parameter und nicht fest verdrahtet, weil die richtige Grenze von der
 * Flotte abhängt — und weil der zugrunde liegende Messwert nicht überall
 * dasselbe bedeutet: `memoryUsedMb` entsteht aus "gesamt minus frei" und zählt
 * damit den Dateisystem-Cache als belegt. Auf einem Entwicklungsrechner und auf
 * einem länger laufenden Linux-Server liegt dieser Wert dauerhaft über 90 %,
 * ohne dass dem Server etwas fehlt.
 *
 * Die Vorgabe bleibt streng. Wer sie lockert, tut es ausdrücklich.
 */
export interface OverloadThresholds {
  readonly cpuPercent: number;
  readonly memoryRatio: number;
  readonly diskRatio: number;
}

/** Höchstalter des letzten Heartbeats, damit ein Node platzierbar bleibt. */
export const MAX_HEARTBEAT_AGE_FOR_PLACEMENT_MS = 30_000;

/**
 * Stufe 1 — Filter.
 * Gibt entweder `null` (Kandidat besteht) oder den Ablehnungsgrund zurück.
 */
export function filterCandidate(
  candidate: PlacementCandidate,
  request: PlacementRequest,
  thresholds: OverloadThresholds = OVERLOAD_THRESHOLDS,
): RejectionReason | null {
  if (request.excludeNodeIds.includes(candidate.nodeId)) {
    return REJECTION_REASONS.explicitlyExcluded;
  }

  if (!isSchedulableStatus(candidate.status)) {
    return REJECTION_REASONS.statusNotSchedulable;
  }

  if (!candidate.schedulable) {
    return REJECTION_REASONS.administrativelyDisabled;
  }

  // Vor der Systemprüfung: Ein zurückgebliebener Agent kennt die neueren
  // Prüfungen womöglich gar nicht und meldet deshalb `fail`. Käme der
  // Preflight-Grund zuerst, schickte die Meldung den Betreiber zu `docker logs`,
  // während in Wahrheit nur ein Update aussteht, das sich von selbst erledigt.
  if (candidate.agentVersionOutdated) {
    return REJECTION_REASONS.agentVersionOutdated;
  }

  // Nach Status und administrativer Sperre, aber vor allem Fachlichen: Wenn ein
  // Mensch den Node abgeschaltet hat, ist das die genauere Auskunft als ein
  // Befund der Systemprüfung, den derselbe Mensch vielleicht gerade behebt.
  if (candidate.preflightFailed) {
    return REJECTION_REASONS.preflightFailed;
  }

  if (candidate.region !== request.region) {
    return REJECTION_REASONS.regionMismatch;
  }

  for (const [key, value] of Object.entries(request.requiredLabels)) {
    if (candidate.labels[key] !== value) {
      return REJECTION_REASONS.labelMismatch;
    }
  }

  // Ein Node ohne Heartbeat wird nie platziert — auch dann nicht, wenn sein
  // gespeicherter Status noch "online" lautet. Der Status wird zyklisch
  // nachgeführt und kann in dem Moment veraltet sein.
  if (candidate.heartbeatAgeMs === null) {
    return REJECTION_REASONS.heartbeatStale;
  }

  if (candidate.heartbeatAgeMs > MAX_HEARTBEAT_AGE_FOR_PLACEMENT_MS) {
    return REJECTION_REASONS.heartbeatStale;
  }

  if (!fitsIntoCapacity(candidate.capacity, candidate.allocated, request.resources)) {
    return REJECTION_REASONS.insufficientCapacity;
  }

  if (candidate.metrics !== null && isOverloaded(candidate.metrics, thresholds)) {
    return REJECTION_REASONS.overloaded;
  }

  return null;
}

/** Prüft die gemessene Ist-Last gegen die Überlastschwellen. */
export function isOverloaded(
  metrics: NodeMetrics,
  thresholds: OverloadThresholds = OVERLOAD_THRESHOLDS,
): boolean {
  return (
    metrics.cpuPercent >= thresholds.cpuPercent ||
    memoryUsageRatio(metrics) >= thresholds.memoryRatio ||
    diskUsageRatio(metrics) >= thresholds.diskRatio
  );
}

/**
 * Stufe 2 — Bewertung eines Kandidaten von 0 bis 1.
 *
 * Grundlage sind die *Reservierungen* (nicht die Messwerte), damit die
 * Entscheidung deterministisch und nachvollziehbar bleibt. Die Messwerte
 * kommen als Dämpfungsfaktor beim CPU-Anteil hinzu; sie sind naturgemäß
 * volatil und dürfen die Verteilung nicht dominieren.
 */
export function scoreCandidate(
  candidate: PlacementCandidate,
  request: PlacementRequest,
): ScoredCandidate {
  const free = calculateFreeCapacity(candidate.capacity, candidate.allocated);

  const totalCpuMilli = candidate.capacity.cpuCores * 1000;
  const freeMemoryRatio = ratio(free.memoryMb, candidate.capacity.memoryMb);
  const freeDiskRatio = ratio(free.diskGb, candidate.capacity.diskGb);
  const freeSlotRatio = ratio(free.siteSlots, candidate.capacity.maxSites);

  // Kopffreiheit der CPU: bevorzugt die Reservierung, korrigiert aber nach
  // unten, wenn die tatsächliche Auslastung höher liegt als gebucht.
  const reservedCpuHeadroom = ratio(free.cpuMilli, totalCpuMilli);
  const measuredCpuHeadroom =
    candidate.metrics === null ? reservedCpuHeadroom : 1 - candidate.metrics.cpuPercent / 100;
  const cpuHeadroom = Math.max(0, Math.min(reservedCpuHeadroom, measuredCpuHeadroom));

  const imageAffinity = calculateImageAffinity(candidate.cachedImages, request.requiredImages);

  const breakdown = {
    freeMemory: freeMemoryRatio * SCORING_WEIGHTS.freeMemory,
    freeDisk: freeDiskRatio * SCORING_WEIGHTS.freeDisk,
    cpuHeadroom: cpuHeadroom * SCORING_WEIGHTS.cpuHeadroom,
    siteSlots: freeSlotRatio * SCORING_WEIGHTS.siteSlots,
    imageAffinity: imageAffinity * SCORING_WEIGHTS.imageAffinity,
  } as const;

  const score = Object.values(breakdown).reduce((sum, value) => sum + value, 0);

  return {
    nodeId: candidate.nodeId,
    hostname: candidate.hostname,
    // Rundung auf sechs Nachkommastellen: verhindert, dass
    // Gleitkomma-Rauschen die Sortierung zweier identischer Nodes
    // zwischen zwei Aufrufen umdreht.
    score: Number(score.toFixed(6)),
    breakdown,
  };
}

/**
 * Anteil der benötigten Images, die bereits lokal vorliegen.
 * Ohne Anforderung gilt neutral 1 — sonst würde ein Blueprint ohne
 * Image-Angabe alle Nodes gleichermaßen abwerten.
 */
export function calculateImageAffinity(
  cachedImages: readonly string[],
  requiredImages: readonly string[],
): number {
  if (requiredImages.length === 0) {
    return 1;
  }

  const cached = new Set(cachedImages);
  const hits = requiredImages.filter((image) => cached.has(image)).length;

  return hits / requiredImages.length;
}

/**
 * Führt Filter und Bewertung aus und liefert das vollständige Ergebnis.
 *
 * Auch die abgelehnten Kandidaten werden zurückgegeben. Das ist der Unterschied
 * zwischen "keine Kapazität frei" und einer Fehlermeldung, mit der man
 * tatsächlich etwas anfangen kann: Der Betreiber sieht, ob alle Nodes in der
 * falschen Region stehen, ob die Heartbeats fehlen oder ob wirklich der
 * Speicher ausgeht.
 */
export function selectNode(
  candidates: readonly PlacementCandidate[],
  request: PlacementRequest,
  thresholds: OverloadThresholds = OVERLOAD_THRESHOLDS,
): PlacementResult {
  const rejected: RejectedCandidate[] = [];
  const eligible: PlacementCandidate[] = [];

  for (const candidate of candidates) {
    const reason = filterCandidate(candidate, request, thresholds);

    if (reason === null) {
      eligible.push(candidate);
    } else {
      rejected.push({
        nodeId: candidate.nodeId,
        hostname: candidate.hostname,
        reason,
        message: REJECTION_REASON_MESSAGES[reason],
      });
    }
  }

  const ranked = eligible
    .map((candidate) => scoreCandidate(candidate, request))
    // Bei Punktgleichstand entscheidet die nodeId. Damit ist die Auswahl
    // vollständig deterministisch und in Tests reproduzierbar.
    .sort((a, b) => (b.score === a.score ? a.nodeId.localeCompare(b.nodeId) : b.score - a.score));

  return {
    selected: ranked[0] ?? null,
    ranked,
    rejected,
  };
}

/** Sicherer Anteil zweier Zahlen; 0/0 ergibt 0 statt NaN. */
function ratio(part: number, total: number): number {
  if (total <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(1, part / total));
}
